import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import OrganizationSwitcher from './OrganizationSwitcher';

interface TopBarProps {
  onMenuClick?: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, login } = useAuth();
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Extract user's name from Auth0 user object
  const userName = user?.name || user?.nickname || user?.email?.split('@')[0] || 'User';
  const userInitials = userName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative flex items-center shadow-lg dark:shadow-2xl z-[100] border-b border-gray-200 dark:border-blue-500/20 transition-colors duration-300" style={{ height: '57.6px' }}>
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Light mode gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-gray-50 to-white dark:from-slate-950 dark:via-blue-950/95 dark:to-slate-950 transition-colors duration-300"></div>

        {/* Moving light wave - dark mode only */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/10 to-transparent opacity-0 dark:opacity-100"
          style={{
            animation: 'topBarWave 15s ease-in-out infinite',
            transform: 'translateX(-100%)'
          }}
        />

        {/* Floating orbs - dark mode only */}
        <div className="absolute top-0 left-1/4 w-32 h-32 bg-purple-500/4 rounded-full blur-3xl animate-pulse opacity-0 dark:opacity-100 pointer-events-none" style={{ animationDuration: '8s' }}></div>
        <div className="absolute top-0 right-1/3 w-24 h-24 bg-cyan-500/4 rounded-full blur-2xl animate-pulse opacity-0 dark:opacity-100 pointer-events-none" style={{ animationDelay: '4s', animationDuration: '10s' }}></div>

      </div>

      <div className="relative w-full px-2 sm:px-6 lg:px-8 flex justify-between items-center min-w-0">
        {/* Enhanced Brand Section */}
        <div className="flex items-center space-x-3 sm:space-x-6">
          {/* Mobile/Tablet Menu Button - Show on small and medium screens */}
          {onMenuClick ? (
            <button
              onClick={onMenuClick}
              className="lg:hidden group relative flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 dark:bg-blue-600/10 border border-gray-200 dark:border-blue-500/20 hover:border-gray-300 dark:hover:border-blue-400/40 transition-all duration-300 hover:scale-110 hover:bg-gray-200 dark:hover:bg-blue-600/20"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-gray-100/0 to-gray-200/0 dark:from-blue-600/0 dark:to-purple-600/0 group-hover:from-gray-100 group-hover:to-gray-200 dark:group-hover:from-blue-600/20 dark:group-hover:to-purple-600/20 rounded-xl transition-all duration-300"></div>
              <svg className="relative w-6 h-6 text-gray-600 dark:text-blue-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          ) : (
            // Invisible spacer to maintain consistent alignment when no menu button
            <div className="lg:hidden w-10 h-10"></div>
          )}

          {/* Logo/Brand with amazing effects */}
          <div
            className="flex items-center space-x-3 cursor-pointer group"
            onClick={() => navigate('/')}
          >
            {/* Enhanced logo container */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 rounded-xl blur-lg opacity-0 group-hover:opacity-30 dark:group-hover:opacity-50 transition-opacity duration-500"></div>
              <div className="relative w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-white to-gray-100 dark:from-slate-900/90 dark:to-blue-900/90 backdrop-blur-xl border border-gray-200 dark:border-blue-500/30 shadow-lg dark:shadow-2xl dark:shadow-blue-500/20 group-hover:scale-110 transition-all duration-500 overflow-hidden">
                <img
                  src="/sentegamesicon.png"
                  alt="Sente"
                  className="w-8 h-8 object-cover rounded-lg"
                />
              </div>
            </div>

            {/* Brand text with gradient animation */}
            <div className="flex flex-col">
              <h1 className="text-xl font-black leading-none group-hover:scale-105 transition-transform duration-300">
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 dark:from-blue-400 dark:via-purple-400 dark:to-cyan-400 bg-clip-text text-transparent animate-gradient bg-300%">
                  Sente
                </span>
              </h1>
              <span className="text-xs text-gray-500 dark:text-blue-300/80 leading-none font-medium group-hover:text-gray-700 dark:group-hover:text-blue-200 transition-colors duration-300">
                Accounts for AI agents
              </span>
            </div>
          </div>

          {/* Organization Switcher - admin-only dropdown, static label for others */}
          {isAuthenticated && <div className="hidden sm:block"><OrganizationSwitcher /></div>}

        </div>

        {/* Enhanced Status Section */}
        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-shrink-0">
          {/* Theme Toggle */}
          <ThemeToggle />

          {/* User Avatar with enhanced dropdown - or Sign In button for unauthenticated users */}
          {isAuthenticated ? (
          <div className="relative flex-shrink-0 z-50" ref={dropdownRef}>
            <button
              onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
              className="group flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-3 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800/60 dark:to-gray-700/60 rounded-xl border border-gray-200 dark:border-gray-600/50 backdrop-blur-sm hover:border-gray-300 dark:hover:border-gray-500/70 transition-all duration-300 hover:scale-105 h-12"
            >
              {/* Avatar with gradient border */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full blur-md opacity-20 dark:opacity-30 group-hover:opacity-40 dark:group-hover:opacity-50 transition-opacity duration-300"></div>
                <div className="relative w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center border border-blue-400/30 shadow-lg">
                  <span className="text-sm font-bold text-white">{userInitials}</span>
                </div>
              </div>

              {/* User info */}
              <div className="flex flex-col text-left">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 leading-none group-hover:text-gray-900 dark:group-hover:text-white transition-colors duration-300">
                  <span className="hidden sm:inline">{userName}</span>
                  <span className="sm:hidden">{userInitials}</span>
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 leading-none hidden sm:block">Operator</span>
              </div>

              {/* Dropdown arrow */}
              <svg
                className={`w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-all duration-300 ${isUserDropdownOpen ? 'rotate-180' : ''
                  }`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Enhanced Dropdown Menu */}
            {isUserDropdownOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700/50 backdrop-blur-xl z-[100] overflow-hidden">
                {/* Animated background - dark mode only */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-purple-600/5 to-cyan-600/5 opacity-0 dark:opacity-100"></div>

                <div className="relative py-2">
                  {/* User Info Section */}
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700/50">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                        <span className="text-sm font-bold text-white">{userInitials}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{userName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Account Status */}
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700/50">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</span>
                      <span className="text-xs font-bold px-2 py-1 rounded-full bg-green-100 dark:bg-green-600/20 text-green-600 dark:text-green-300 border border-green-200 dark:border-green-500/30">
                        Active Session
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="px-2 py-2 space-y-1">
                    <button
                      onClick={() => {
                        navigate('/settings');
                        setIsUserDropdownOpen(false);
                      }}
                      className="w-full group flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gradient-to-r hover:from-blue-100 hover:to-blue-50 dark:hover:from-blue-600/20 dark:hover:to-blue-500/20 rounded-lg transition-all duration-300"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-600/20 group-hover:bg-blue-200 dark:group-hover:bg-blue-600/30 transition-colors duration-300">
                          <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span>Settings</span>
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        logout();
                        setIsUserDropdownOpen(false);
                      }}
                      className="w-full group flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gradient-to-r hover:from-red-100 hover:to-red-50 dark:hover:from-red-600/20 dark:hover:to-red-500/20 rounded-lg transition-all duration-300"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-1.5 rounded-lg bg-red-100 dark:bg-red-600/20 group-hover:bg-red-200 dark:group-hover:bg-red-600/30 transition-colors duration-300">
                          <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span>Logout</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          ) : (
            /* Sign In button for unauthenticated users */
            <button
              onClick={() => login()}
              className="group flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-xl font-medium transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-500/25 hover:shadow-blue-400/40 h-11"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
              </svg>
              <span>Sign In</span>
            </button>
          )}
        </div>
      </div>

      {/* Animated bottom border - dark mode only */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-gray-300 dark:via-blue-500/50 to-transparent overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-400 dark:via-blue-400 to-transparent opacity-0 dark:opacity-100"
          style={{
            animation: 'topBarBorderMove 8s ease-in-out infinite',
            transform: 'translateX(-100%)'
          }}
        />
      </div>

      {/* CSS animations */}
      <style>
        {`
          @keyframes topBarWave {
            0% {
              transform: translateX(-100%);
              opacity: 0;
            }
            50% {
              opacity: 1;
            }
            100% {
              transform: translateX(100%);
              opacity: 0;
            }
          }
          
          @keyframes topBarBorderMove {
            0% {
              transform: translateX(-100%);
              opacity: 0;
            }
            50% {
              opacity: 1;
            }
            100% {
              transform: translateX(100%);
              opacity: 0;
            }
          }

          @keyframes gradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }

          .animate-gradient {
            animation: gradient 4s ease infinite;
          }

          .bg-300\\% {
            background-size: 300% 300%;
          }
        `}
      </style>

    </div>
  );
};

export default TopBar;